import React, {useCallback, useEffect, useRef, useState} from 'react';

import {
    FormatBold as FormatBoldIcon,
    FormatItalic as FormatItalicIcon,
    // TextDecrease as TextDecreaseIcon,
    // FormatStrikethrough as FormatStrikethroughIcon,
    // FormatAlignLeft as FormatAlignLeftIcon,
    // FormatAlignCenter as FormatAlignCenterIcon,
    // FormatAlignRight as FormatAlignRightIcon,
    // TextIncrease as TextIncreaseIcon,
} from '@mui/icons-material';
import {
    // ButtonGroup,
    Grid,
    // IconButton,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup} from '@mui/material';

import {getFormats, getTextContent} from 'canvas/Drawer/text-utils';
import {useKeyboardStore} from 'store';

const styles = {

    stackStyle: {
        backgroundColor: 'grey.dark',
        borderRadius: '6px',
        margin: '10px 10px 0 10px',
    },

    inputContainer: {
        marginLeft: '10px',
        marginRight: '10px',
        marginBottom: '10px',
    },

    toggleButton: {
        padding: '12px',
        color: 'white.main',
    }
};

const Edit = ({text}) => {

    // const theme = useTheme();
    // const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const inputRef = useRef();
    const textObjectRef = useRef(text);
    const isKeyboardInputRef = useRef(false);
    const [content, setContent] = useState(() => getTextContent(text));
    const [formats, setFormats] = useState(getFormats(text));
    // const [textAlign, setTextAlign] = useState(text.textAlign);

    const showKeyboard = useKeyboardStore(state => state.show);

    const updateText = useCallback((value, fromKeyboard = false) => {
        if (fromKeyboard) {
            isKeyboardInputRef.current = true;
            
            // Keep the flag active longer to prevent blur
            setTimeout(() => {
                isKeyboardInputRef.current = false;
            }, 200);
        }
        
        setContent(value);
        // Update the text object directly and render canvas immediately
        if (textObjectRef.current && textObjectRef.current.custom) {
            textObjectRef.current.set('text', value);
            // Render canvas on every keystroke
            if (textObjectRef.current.canvas) {
                textObjectRef.current.canvas.renderAll();
            }
        }
    }, []);

    const handleBlur = useCallback((e) => {
        // Don't process blur if it's from keyboard input
        if (isKeyboardInputRef.current) {
            // Prevent default blur behavior
            e.preventDefault();
            e.stopPropagation();

            // Refocus the input immediately
            if (inputRef.current) {
                setTimeout(() => {
                    inputRef.current.focus();
                }, 0);
            }

            return;
        }
        
        // Canvas already rendered on keystroke, just ensure final render
        if (textObjectRef.current && textObjectRef.current.canvas) {
            textObjectRef.current.canvas.renderAll();
        }
    }, []);

    const onInputFocus = useCallback((e) => {
        const rect = e.target.getBoundingClientRect();

        const keyboardUpdateText = (value, caretPosition) => {
            // Mark as keyboard input
            isKeyboardInputRef.current = true;
            
            // Update the content
            setContent(value);
            
            // Update the text object directly and render canvas immediately
            if (textObjectRef.current && textObjectRef.current.custom) {
                textObjectRef.current.set('text', value);
                // Render canvas on every keystroke
                if (textObjectRef.current.canvas) {
                    textObjectRef.current.canvas.renderAll();
                }
            }

            // Restore cursor position and keep focus
            setTimeout(() => {
                if (inputRef.current && caretPosition !== null) {
                    inputRef.current.focus();
                    
                    if (inputRef.current.setSelectionRange) {
                        inputRef.current.setSelectionRange(caretPosition, caretPosition);
                    }
                }
                
                isKeyboardInputRef.current = false;
            }, 0);
        };

        showKeyboard({
            value: e.target.value,
            top: rect.top + rect.height + 20,
            left: 0,
            layout: 'default',
            hideOnEnter: false,
            onInput: keyboardUpdateText,
        });
    }, [showKeyboard]);

    const handleSelect = useCallback((_e) => {
        const target = _e.target;
        if (textObjectRef.current) {
            textObjectRef.current.setSelectionStart(target.selectionStart);
            textObjectRef.current.setSelectionEnd(target.selectionEnd);
        }
    }, []);

    const formatChanged = useCallback((_e, newFormats) => {
        setFormats(newFormats);
    }, []);

    useEffect(() => {
        // Update text object ref and state when text prop changes
        textObjectRef.current = text;
        setContent(getTextContent(text));
        setFormats(getFormats(text));
    }, [text]);

    useEffect(() => {
        if (formats.length === 0) {
            if (textObjectRef.current) {
                textObjectRef.current.styles = {};
            }
        }

        // if text selected
        if (textObjectRef.current && textObjectRef.current.selectionStart !== textObjectRef.current.selectionEnd) {
            let lineStyleObj = Object.keys(textObjectRef.current.styles).length ? textObjectRef.current.styles : {0: {}};

            for (let i = textObjectRef.current.selectionStart; i < textObjectRef.current.selectionEnd; i++) {
                lineStyleObj[0][i] = {};
                lineStyleObj[0][i].fontWeight = formats.includes('bold') ? 'bold' : 'normal' ;
                lineStyleObj[0][i].fontStyle = formats.includes('italic') ? 'italic' : 'normal' ;
                lineStyleObj[0][i].linethrough = formats.includes('linethrough');
            }

            textObjectRef.current.styles = lineStyleObj;
        }
        else if (textObjectRef.current) {
            textObjectRef.current.fontWeight = formats.includes('bold') ? 'bold' : 'normal';
            textObjectRef.current.fontStyle = formats.includes('italic') ? 'italic' : 'normal';
            textObjectRef.current.linethrough = formats.includes('linethrough');
        }

        if (textObjectRef.current && textObjectRef.current.canvas) {
            textObjectRef.current.canvas.renderAll();
        }
    }, [formats]);

    // const textAlignChanged = (_e, textAlign) => {
    //     text.textAlign = textAlign;
    //     text.canvas.renderAll();
    //     setTextAlign(textAlign);
    // };

    /*const changeFontSize = (delta) => {
        if (delta < 0 && text.fontSize <= 20) {
            return;
        }

        if (delta > 0 && text.fontSize > 60) {
            return;
        }

        // text.setSelectionStyles({
        //     fontSize: text.fontSize + delta,
        // });
        text.fontSize = text.fontSize + delta;
        text.canvas.renderAll();
    };*/

    return <Grid container direction="column" spacing={0}>
        <Grid item>

            <Stack direction="row" sx={styles.stackStyle}>
                <ToggleButtonGroup value={formats} onChange={formatChanged}>
                    <ToggleButton sx={styles.toggleButton} value="bold"><FormatBoldIcon/></ToggleButton>
                    <ToggleButton sx={styles.toggleButton} value="italic"><FormatItalicIcon/></ToggleButton>
                    {/*<ToggleButton sx={styles.toggleButton} value="linethrough"><FormatStrikethroughIcon/></ToggleButton>*/}
                </ToggleButtonGroup>

                {/*
                <Divider flexItem orientation="vertical" sx={{mx: 1, my: 1, borderColor: ''}}/>

                <ToggleButtonGroup sx={styles.toggleButtonGroup} value={formats} onChange={textAlignChanged}>
                    <ToggleButton sx={styles.toggleButton} value="left"><FormatAlignLeftIcon/></ToggleButton>
                    <ToggleButton sx={styles.toggleButton} value="center"><FormatAlignCenterIcon/></ToggleButton>
                    <ToggleButton sx={styles.toggleButton} value="right"><FormatAlignRightIcon/></ToggleButton>
                </ToggleButtonGroup>

                <ButtonGroup sx={styles.toggleButtonGroup}>
                    <IconButton
                        sx={styles.toggleButton}
                        onClick={() => changeFontSize(+4)}><TextIncreaseIcon/></IconButton>
                    <IconButton
                        sx={styles.toggleButton}
                        onClick={() => changeFontSize(-4)}><TextDecreaseIcon/></IconButton>
                </ButtonGroup>
                */}
            </Stack>
        </Grid>

        <Grid item sx={styles.inputContainer}>

            <TextField
                name="content"
                value={content}
                inputRef={inputRef}
                inputProps={{
                    sx: [
                        formats.includes('bold') && {fontWeight: 'bold'},
                        formats.includes('italic') && {fontStyle: 'italic'},
                    ]
                }}
                multiline
                rows={4}
                fullWidth
                autoComplete="off"
                autoFocus
                onChange={e => updateText(e.target.value)}
                onBlur={handleBlur}
                onMouseDown={e => {
                    // Prevent blur when clicking on keyboard
                    if (isKeyboardInputRef.current) {
                        e.preventDefault();
                    }
                }}
                onFocus={e => {
                    setTimeout(() => {
                        onInputFocus(e);
                    }, 100);
                }}
                variant="outlined"
                sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                        border: '2px solid #777 !important',
                    },
                }}
                onSelect={handleSelect}
            />
        </Grid>
    </Grid>;
};

// Use a custom comparison function to ensure re-render when text object changes
const arePropsEqual = (prevProps, nextProps) => {
    // Always re-render if text object reference changes
    return prevProps.text === nextProps.text;
};

export default React.memo(Edit, arePropsEqual);